import { useEffect, useRef, type FormEvent } from 'react';
import {
  Button,
  Form,
  PanelFooter,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Stack,
} from '@customerio/pluma-components/react';
import { useSignupFlow } from '../signup-flow';
import { SignupAnalyticsEvent, startupCompleteSubmittedProps, startupCompleteViewedProps } from '../utils/analytics';

/** Terminal step for startup signups; submitting creates the account. */
export function StartupCompleteStep() {
  const { effectiveSignupType, resolvedSignupType, goToNextStep, isCompletingSignup, trackSignupEvent } =
    useSignupFlow();

  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) {
      return;
    }
    viewedRef.current = true;
    trackSignupEvent(
      SignupAnalyticsEvent.StartupCompleteViewed,
      startupCompleteViewedProps({ signupType: effectiveSignupType, resolvedSignupType }),
    );
  }, [trackSignupEvent, effectiveSignupType, resolvedSignupType]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    trackSignupEvent(
      SignupAnalyticsEvent.StartupCompleteSubmitted,
      startupCompleteSubmittedProps({ signupType: effectiveSignupType, resolvedSignupType }),
    );
    return goToNextStep();
  };

  return (
    <>
      <PanelHeader>
        <PanelTitle level="1">Startup Program application received</PanelTitle>
      </PanelHeader>
      <PanelSection data-testid="signup-step-startup-complete">
        <Form onSubmit={handleSubmit}>
          <Stack gap="200">
            <Paragraph variant="secondary">
              Your 14-day free trial starts today, so you can explore Customer.io, build automations, and send messages.
            </Paragraph>
            <Paragraph variant="secondary">
              Our team will review your application and email you an update in the next few days.
            </Paragraph>
          </Stack>
          <PanelFooter justifyContent="flex-end">
            <Button
              type="submit"
              variant="primary"
              icon="chevron-right"
              iconPosition="trailing"
              isLoading={isCompletingSignup}
            >
              Start building
            </Button>
          </PanelFooter>
        </Form>
      </PanelSection>
    </>
  );
}
